import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/shared';
import {
  bulkDisableUsers,
  loadAccessControls,
  updatePrivateMode,
  updateUserAccess,
  type AccessControlData,
  type AccessControlUser,
} from '@services/accessControl';
import './MetricsDevAccessPanel.css';

type Confirmation =
  | { type: 'bulk' }
  | { type: 'open' }
  | { type: 'disable'; user: AccessControlUser }
  | null;

const SUPABASE_AUTH_SETTINGS =
  'https://supabase.com/dashboard/project/rbbeebzzlwoylevdhxyx/auth/providers';

function formatDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

export function MetricsDevAccessPanel() {
  const [data, setData] = useState<AccessControlData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [confirmationText, setConfirmationText] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await loadAccessControls());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load access controls');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedUsers = useMemo(() => {
    return [...(data?.users || [])].sort((a, b) => {
      if (a.isProtected !== b.isProtected) return a.isProtected ? -1 : 1;
      if (a.effectiveAccess !== b.effectiveAccess) return a.effectiveAccess ? -1 : 1;
      return (a.email || a.userId).localeCompare(b.email || b.userId);
    });
  }, [data]);

  const beginConfirmation = (next: Confirmation) => {
    setConfirmation(next);
    setConfirmationText('');
    setError(null);
    setNotice(null);
  };

  const mutate = async (action: () => Promise<string>) => {
    setIsMutating(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      setConfirmation(null);
      setConfirmationText('');
      await load();
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : 'Access update failed');
    } finally {
      setIsMutating(false);
    }
  };

  const requiredText = confirmation?.type === 'bulk'
    ? 'DISABLE NON-PROTECTED USERS'
    : confirmation?.type === 'open'
      ? 'OPEN NINJACASH'
      : confirmation?.type === 'disable'
        ? (confirmation.user.email || '')
        : '';

  const confirmAction = () => {
    if (!confirmation || confirmationText !== requiredText) return;
    if (confirmation.type === 'bulk') {
      void mutate(async () => {
        const count = await bulkDisableUsers(confirmationText);
        return `Disabled ${count} non-protected account${count === 1 ? '' : 's'}.`;
      });
    } else if (confirmation.type === 'open') {
      void mutate(async () => {
        await updatePrivateMode(false, confirmationText);
        return 'Private Mode is off. Explicitly banned users remain disabled.';
      });
    } else {
      const target = confirmation.user;
      void mutate(async () => {
        await updateUserAccess(target.userId, false, confirmationText);
        return `${target.email || target.userId} is disabled and banned.`;
      });
    }
  };

  return (
    <section className="access-panel" aria-label="Platform access controls">
      <div className="access-panel-header">
        <div>
          <h2>Platform Access</h2>
          <p>Default-deny controls for signups and existing accounts.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={load} disabled={isLoading || isMutating}>
          {isLoading ? 'Checking...' : 'Refresh access'}
        </Button>
      </div>

      {error && <div className="access-message error">{error}</div>}
      {notice && <div className="access-message success">{notice}</div>}

      {data && (
        <>
          <div className="access-status-grid">
            <div className={`access-status-card ${data.signupDisabled === true ? 'safe' : 'danger'}`}>
              <span>New signups</span>
              <strong>{data.signupDisabled === true ? 'BLOCKED' : data.signupDisabled === false ? 'OPEN' : 'UNKNOWN'}</strong>
              <small>Supabase global setting</small>
              <a href={SUPABASE_AUTH_SETTINGS} target="_blank" rel="noreferrer">Open Supabase setting</a>
            </div>
            <div className={`access-status-card ${data.privateMode ? 'safe' : 'warning'}`}>
              <span>Private Mode</span>
              <strong>{data.privateMode ? 'ON' : 'OFF'}</strong>
              <small>{data.privateMode ? 'Only protected or manually enabled accounts pass' : 'Unbanned accounts can use the app'}</small>
              {data.privateMode ? (
                <button type="button" onClick={() => beginConfirmation({ type: 'open' })}>Turn off...</button>
              ) : (
                <button
                  type="button"
                  onClick={() => void mutate(async () => {
                    await updatePrivateMode(true);
                    return 'Private Mode is on.';
                  })}
                >Turn on now</button>
              )}
            </div>
            <div className="access-status-card safe">
              <span>Protected accounts</span>
              <strong>{data.summary.protected}</strong>
              <small>{data.ownerEmails.length} owners + {data.summary.protected - data.ownerEmails.length} test accounts</small>
            </div>
            <div className="access-status-card">
              <span>Current access</span>
              <strong>{data.summary.enabled} / {data.summary.total}</strong>
              <small>{data.summary.disabled} disabled</small>
            </div>
          </div>

          <div className="access-bulk-row">
            <div>
              <strong>Emergency lock</strong>
              <span>Ban every account except the five protected emails. Data is preserved.</span>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => beginConfirmation({ type: 'bulk' })}
              disabled={isMutating}
            >Disable all others...</Button>
          </div>

          {confirmation && (
            <div className="access-confirmation">
              <div>
                <strong>Confirmation required</strong>
                <span>Type <code>{requiredText}</code> exactly.</span>
              </div>
              <input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                aria-label="Access action confirmation"
              />
              <Button
                variant="danger"
                size="sm"
                onClick={confirmAction}
                disabled={isMutating || confirmationText !== requiredText}
              >{isMutating ? 'Applying...' : 'Confirm'}</Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmation(null)} disabled={isMutating}>
                Cancel
              </Button>
            </div>
          )}

          <div className="access-user-table-wrap">
            <table className="access-user-table">
              <thead>
                <tr><th>Account</th><th>Role</th><th>Access</th><th>Last sign-in</th><th>Action</th></tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.userId}>
                    <td><strong>{user.email || user.userId}</strong></td>
                    <td>{user.isOwner ? 'Owner' : user.isProtected ? 'Protected test' : 'User'}</td>
                    <td>
                      <span className={`access-pill ${user.effectiveAccess ? 'enabled' : 'disabled'}`}>
                        {user.effectiveAccess ? 'ENABLED' : 'DISABLED'}
                      </span>
                      {user.bannedUntil && <small className="access-ban-date">banned to {formatDate(user.bannedUntil)}</small>}
                    </td>
                    <td>{formatDate(user.lastSignInAt)}</td>
                    <td>
                      {user.isProtected ? (
                        <span className="access-protected-label">Protected</span>
                      ) : user.effectiveAccess ? (
                        <button type="button" className="access-row-action danger" onClick={() => beginConfirmation({ type: 'disable', user })}>
                          Disable
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="access-row-action"
                          onClick={() => void mutate(async () => {
                            await updateUserAccess(user.userId, true);
                            return `${user.email || user.userId} is enabled and unbanned.`;
                          })}
                        >Enable</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
